"use client";

import { setCookie } from "cookies-next/client";
import { Check, NavArrowDown } from "iconoir-react";
import { useLocale } from "next-intl";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link, usePathname } from "@/i18n/navigation";
import { languages } from "@/lib/languages";
import { cn } from "@/lib/utils";

type Language = (typeof languages)[number];

function persistLocale(locale: string) {
  setCookie("wf-locale", locale, { path: "/", sameSite: "lax" });
}

export function LanguageMenu({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  const pathname = usePathname();
  const currentLocale = useLocale();

  return (
    // Non-modal so opening the menu does not lock body scroll and hide the
    // scrollbar — that was shifting the footer (and header) horizontally.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className={cn(
          "text-ds-body-sm text-grey-600 hover:text-grey-900 focus-visible:outline-pulse-700 focus-visible:rounded-ds-xs inline-flex items-center gap-1 focus-visible:outline-2 focus-visible:outline-offset-2",
          className
        )}
      >
        {label}
        <NavArrowDown aria-hidden className="size-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        id="language-selector-dropdown"
        className={cn(
          "bg-grey-0 border-grey-100 shadow-ds-sm rounded-ds-m min-w-[11rem] overflow-hidden border p-1",
          "data-closed:duration-[180ms] data-open:duration-[220ms]",
          "data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1"
        )}
      >
        {languages.map((lang: Language) => {
          const selected = lang.value === currentLocale;
          return (
            <DropdownMenuItem
              key={lang.value}
              className="rounded-ds-s p-0 text-base focus:bg-transparent"
              render={
                <Link
                  href={pathname}
                  locale={lang.value}
                  onClick={() => persistLocale(lang.value)}
                  className={cn(
                    "text-ds-body-sm flex w-full cursor-pointer items-center justify-between gap-3 px-2 py-1.5 outline-none",
                    "text-grey-600 hover:bg-grey-50 hover:text-grey-900 focus-visible:bg-grey-50 focus-visible:text-grey-900",
                    selected && "text-pulse-700 font-medium"
                  )}
                />
              }
            >
              {lang.label}
              {selected && (
                <Check aria-hidden className="text-pulse-700 size-4 shrink-0" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
